import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { formatDate } from '@/lib/format';
import {
  subscribeBilling,
  subscribeTenantInvoices,
  type BillingDocResult,
  type InvoiceListResult,
} from '@/lib/firestoreTenantDetail';
import { BillingCard } from '@/features/billing/BillingCard';
import { overviewStats } from '@/features/billing/overviewStats';

/**
 * Tela `/billing/overview/:tenantId`: assinatura, próxima fatura e total em
 * aberto de um tenant, com atalhos para faturas e meios de pagamento.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (Sprint 16 —
 * rotas /billing/* + sub-menu Cobrança).
 *
 * Por quê: resumo de cobrança de um tenant só, sem abrir o detalhe inteiro
 * do tenant (`/tenants/:id`).
 *
 * Contrato: dois listeners — `subscribeBilling` (doc da assinatura,
 * renderizado pelo `BillingCard`) e `subscribeTenantInvoices` (até 100
 * faturas, `issued_at desc`). Deriva no cliente: próxima fatura = menor
 * `due_at` entre `PENDING`/`OVERDUE`; total em aberto = soma de
 * `amount_due` desses status, UMA LINHA POR MOEDA (`overviewStats`). Sem callable e sem escrita (trocar ciclo saiu
 * na #679 F4). Sem `tenantId` na rota → mensagem "selecione uma empresa".
 *
 * Usado por: `routes/router.tsx` (rota `billing/overview/:tenantId`, dentro
 * do `RequireSuperAdmin`). Entrada: botão "Visão geral de cobrança" da faixa
 * de ações do detalhe do tenant (`features/billing/BillingActions.tsx`, aba
 * Cobrança, desde 2026-10-13 — #935 item 23). Sem item de menu, porque pede
 * tenant. Só web. Spec: `docs/screens/billing-overview.md`.
 *
 * Arquitetura: regras `match /billing/{document=**}` e
 * `match /invoices/{invoiceId}` sob `tenants/{tenantId}` (Lê: membro ou
 * super-admin; Escreve: só servidor). Preço vem do doc congelado
 * `billing/subscription`, nunca do catálogo.
 *
 * Dados: `tenants/{tid}/invoices/{id}` (`InvoiceSchema`): `amount_due` em
 * CENTAVOS (o `MoneyDisplay` divide por 100), `currency` BRL | USD | EUR,
 * `due_at` em ms epoch.
 *
 * Falhas conhecidas:
 *  - Até 2026-10-13 (#935, item 3; achado da #798): o "total em aberto"
 *    somava `amount_due` de todas as faturas pendentes e exibia na moeda da
 *    primeira — BRL e EUR viravam um número só. Hoje é um total por moeda.
 *  - Até 2026-10-13 (#935, item 23): a rota não tinha link de entrada.
 *  - Só considera as 100 faturas mais recentes.
 *
 * Issues: #679 (F4 — trocar ciclo saiu da tela), #935 (itens 3 e 23).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 *  - 2026-10-13 10:36 +0200 — (#935) total em aberto por moeda; link de
 *    entrada no detalhe do tenant.
 */
export function BillingOverviewPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const [billing, setBilling] = useState<BillingDocResult>({ kind: 'LOADING' });
  const [invoices, setInvoices] = useState<InvoiceListResult>({ kind: 'LOADING' });

  useEffect(() => {
    if (!tenantId) return;
    setBilling({ kind: 'LOADING' });
    return subscribeBilling(tenantId, setBilling);
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    setInvoices({ kind: 'LOADING' });
    return subscribeTenantInvoices(tenantId, setInvoices);
  }, [tenantId]);

  // Bug corrigido em 2026-10-13 10:36 +0200 (#935, item 3): o total somava
  // `amount_due` de faturas em moedas diferentes e mostrava na moeda da
  // primeira. A conta agora é por moeda, em `overviewStats` (testada lá).
  const stats = useMemo(
    () => (invoices.kind === 'READY' ? overviewStats(invoices.invoices) : null),
    [invoices],
  );

  if (!tenantId) {
    return <Box color="text-body-secondary">{t('billing.overview.missingTenant')}</Box>;
  }


  return (
    <ContentLayout
      header={<Header variant="h1" description={tenantId}>{t('billing.overview.title')}</Header>}
    >
      <SpaceBetween size="l">
        <ColumnLayout columns={3}>
          <BillingCard result={billing} tenantId={tenantId} />

          <Container header={<Header variant="h3">{t('billing.overview.nextInvoice')}</Header>}>
            {invoices.kind === 'LOADING' ? (
              <Spinner />
            ) : stats?.nextDue && stats.nextDueCurrency ? (
              <SpaceBetween size="xxs">
                <Box variant="h2">
                  <MoneyDisplay
                    amount={stats.nextDueAmount}
                    currency={stats.nextDueCurrency}
                    locale={i18n.language}
                  />
                </Box>
                <Box fontSize="body-s" color="text-status-inactive">
                  {t('billing.overview.dueAt', { date: formatDate(stats.nextDue, i18n.language) })}
                </Box>
              </SpaceBetween>
            ) : (
              <Box color="text-body-secondary">{t('billing.overview.noPending')}</Box>
            )}
          </Container>

          <Container header={<Header variant="h3">{t('billing.overview.totalDue')}</Header>}>
            {invoices.kind === 'LOADING' ? (
              <Spinner />
            ) : stats && stats.totals.length > 0 ? (
              <SpaceBetween size="xxs">
                {stats.totals.map((total) => (
                  <Box key={total.currency} variant="h2">
                    <MoneyDisplay amount={total.amount} currency={total.currency} locale={i18n.language} />
                  </Box>
                ))}
              </SpaceBetween>
            ) : (
              <Box color="text-body-secondary">{t('billing.overview.noDue')}</Box>
            )}
          </Container>
        </ColumnLayout>

        <SpaceBetween direction="horizontal" size="xs">
          {/* Trocar ciclo saiu na #679 F4: é o consultor, pela Contratação do
              CRM (F5). A callable só atendia o OWNER e recusava o super-admin. */}
          <Button
            href={`/billing/invoices/${tenantId}`}
            onFollow={(e): void => {
              e.preventDefault();
              void navigate(`/billing/invoices/${tenantId}`);
            }}
          >
            {t('billing.overview.viewInvoices')}
          </Button>
          <Button
            href={`/billing/methods/${tenantId}`}
            onFollow={(e): void => {
              e.preventDefault();
              void navigate(`/billing/methods/${tenantId}`);
            }}
          >
            {t('billing.overview.viewMethods')}
          </Button>
        </SpaceBetween>
      </SpaceBetween>

    </ContentLayout>
  );
}
